import { getTranslation } from '@easy-expense/intl-client';
import React from 'react';

import { Survey } from '../../components/OnboardingFlow/Survey/Survey';

export const AccountUsageSurvey: React.FC<{
  connectedAccount?: string;
  selectedOptions: string[];
  onSelect: (options: string[]) => void;
}> = ({ selectedOptions, onSelect, connectedAccount }) => {
  return (
    <Survey
      header={getTranslation(
        `What type of purchases do you make on ${connectedAccount ? `${connectedAccount}?` : 'this account ?'}`,
      )}
      text={getTranslation('This helps us categorize your transactions accurately')}
      options={[
        {
          title: getTranslation('All Business'),
          success: getTranslation('We will categorize all transactions as business expenses'),
        },
        {
          title: getTranslation('Mostly Business'),
          success: getTranslation('We will help you identify business transactions'),
        },
        {
          title: getTranslation('Mixed'),
          success: getTranslation('We will help you separate business and personal expenses'),
        },
        {
          title: getTranslation('Mostly Personal'),
          success: getTranslation('We will help you identify business transactions'),
        },
        {
          title: getTranslation('All Personal'),
          success: getTranslation('We will categorize all transactions as personal expenses'),
        },
      ]}
      multiSelect={false}
      onOptionSelected={onSelect}
      selectedOptions={selectedOptions}
    />
  );
};

export const WorkStartYearSurvey: React.FC<{
  selectedOptions: string[];
  onSelect: (options: string[]) => void;
}> = ({ selectedOptions, onSelect }) => {
  const currentYear = new Date().getFullYear();
  const lastYear = currentYear - 1;

  return (
    <Survey
      header={getTranslation('When did you start working as a freelancer?')}
      text={getTranslation('This helps us provide relevant tax guidance')}
      options={[
        {
          title: getTranslation(`${currentYear}`),
          key: `${currentYear}`,
          success: getTranslation('We will help you set up proper business expense tracking'),
        },
        {
          title: getTranslation(`${lastYear}`),
          key: `${lastYear}`,
          success: getTranslation('We can help you compare expenses year over year'),
        },
        {
          title: getTranslation('2023 or Earlier'),
          key: '2023',
          success: getTranslation('We can help you analyze historical expense patterns'),
        },
      ]}
      multiSelect={false}
      onOptionSelected={onSelect}
      selectedOptions={selectedOptions}
    />
  );
};

export const WorkStartMonthSurvey: React.FC<{
  selectedOptions: string[];
  onSelect: (options: string[]) => void;
}> = ({ selectedOptions, onSelect }) => {
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  return (
    <Survey
      header={getTranslation('In what month did you start work?')}
      text={getTranslation('This helps us calculate your tax obligations accurately')}
      options={months.map((month) => ({
        title: getTranslation(month),
      }))}
      multiSelect={false}
      onOptionSelected={onSelect}
      selectedOptions={selectedOptions}
    />
  );
};

export const StudentLoanSurvey: React.FC<{
  selectedOptions: string[];
  onSelect: (options: string[]) => void;
}> = ({ selectedOptions, onSelect }) => {
  return (
    <Survey
      header={getTranslation('Do you have student loans?')}
      text={getTranslation('This helps us identify potential tax deductions')}
      options={[
        {
          title: getTranslation('Yes'),
          success: getTranslation('You may be eligible for student loan interest deductions'),
        },
        {
          title: getTranslation('No'),
          success: getTranslation('We will focus on other deductions'),
        },
      ]}
      multiSelect={false}
      onOptionSelected={onSelect}
      selectedOptions={selectedOptions}
    />
  );
};

export const HousingStatusSurvey: React.FC<{
  selectedOptions: string[];
  onSelect: (options: string[]) => void;
}> = ({ selectedOptions, onSelect }) => {
  return (
    <Survey
      header={getTranslation('Are you a home owner or renter?')}
      text={getTranslation('This helps us identify housing-related deductions')}
      options={[
        {
          title: getTranslation('Home Owner'),
          success: getTranslation(
            'You may be eligible for mortgage interest and property tax deductions',
          ),
        },
        {
          title: getTranslation('Renter'),
          success: getTranslation('You may be eligible for rent-related deductions in some states'),
        },
        {
          title: getTranslation('Both'),
          success: getTranslation(
            'We will help you track deductions for both owned and rented properties',
          ),
        },
        {
          title: getTranslation('Neither'),
          success: getTranslation('We will focus on other deductions'),
        },
      ]}
      multiSelect={false}
      onOptionSelected={onSelect}
      selectedOptions={selectedOptions}
    />
  );
};

export const DependentsSurvey: React.FC<{
  selectedOptions: string[];
  onSelect: (options: string[]) => void;
}> = ({ selectedOptions, onSelect }) => {
  return (
    <Survey
      header={getTranslation('Do you have any dependents?')}
      text={getTranslation('This helps us identify family-related tax benefits')}
      options={[
        {
          title: getTranslation('Yes'),
          success: getTranslation('You may be eligible for dependent care and child tax credits'),
        },
        {
          title: getTranslation('No'),
          success: getTranslation('We will focus on other tax benefits'),
        },
      ]}
      multiSelect={false}
      onOptionSelected={onSelect}
      selectedOptions={selectedOptions}
    />
  );
};
